// ================================================
// FILE: src/components/MobileFooter.jsx
// ================================================

import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { auth, db } from "../firebase";
import { getManagerOwner, onManagerModeChange } from "../lib/managerMode";
import { canManagerSeePage } from "../access/pageAccess";

// ---- Route filters ----
const HIDE_PATTERNS = [
  /^\/login(?:\/?|$)/i,
  /^\/register(?:\/?|$)/i,
  /^\/plans(?:\/?|$)/i,
  /^\/payment(?:-|\/?|$)/i,
  /^\/post-payment(?:\/?|$)/i,
  /^\/verify(?:-|\/?|$)/i,
  /^\/verification(?:\/?|$)/i,
  /^\/reset-password(?:\/?|$)/i,
  /^\/forgot(?:-|\/?|$)/i,
];
function shouldHide(pathname) { return HIDE_PATTERNS.some((re) => re.test(pathname || "")); }

// ---- Inline icons ----
function IconBook(props) {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" {...props}>
      <path fill="currentColor" d="M6 2h12a2 2 0 0 1 2 2v18l-4-2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2zm2 6h8v2H8V8zm0 4h8v2H8v-2z" />
    </svg>
  );
}

function IconDoc(props) {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" {...props}>
      <path fill="currentColor" d="M7 2h7l5 5v15a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2zm7 1v5h5" />
    </svg>
  );
}

function IconHome(props) {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" {...props}>
      <path fill="currentColor" d="M12 3 2 11h2v9h6v-6h4v6h6v-9h2L12 3z" />
    </svg>
  );
}
function IconUsers(props) {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" {...props}>
      <path fill="currentColor" d="M16 11a4 4 0 1 0-4-4 4 4 0 0 0 4 4ZM8 12a3 3 0 1 0-3-3 3 3 0 0 0 3 3Zm8 2c-3 0-9 1.5-9 4.5V21h18v-2.5C25 15.5 19 14 16 14Zm-8 0c-.6 0-1.3.1-2 .2A7.2 7.2 0 0 1 1 19v2h7v-2.5a6.7 6.7 0 0 1 0-.5Z" />
    </svg>
  );
}
function IconHelp(props) {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" {...props}>
      <path fill="currentColor" d="M12 2a10 10 0 1 0 .001 20.001A10 10 0 0 0 12 2Zm0 16.75a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5ZM11 13c0-1.13.52-1.74 1.41-2.22.83-.46 1.09-.73 1.09-1.28 0-.71-.63-1.18-1.5-1.18-.9 0-1.5.5-1.5 1.46H8.5c0-1.98 1.63-3.28 3.6-3.28 2.02 0 3.65 1.18 3.65 2.97 0 1.24-.63 1.94-1.98 2.63-.63.33-.77.59-.77 1.1V13h-2z" />
    </svg>
  );
}
function IconLogout(props) {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" {...props}>
      <path fill="currentColor" d="M16 17v-3H9v-4h7V7l5 5-5 5ZM14 19H5V5h9V3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h9Z" />
    </svg>
  );
}

export default function MobileFooter() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [isAuthed, setIsAuthed] = useState(!!auth.currentUser);
  const [userProfile, setUserProfile] = useState(null);


  // Manager-mode: subscribe to grants so footer updates immediately.
  const [mm, setMm] = useState(() => getManagerOwner());
  const [grant, setGrant] = useState(null);

  useEffect(() => onManagerModeChange(setMm), []);

  useEffect(() => {
    if (!mm?.ownerUid || !mm?.managerUid) {
      setGrant(null);
      return;
    }
    const id = `${mm.ownerUid}_${mm.managerUid}`;
    const unsub = onSnapshot(
      doc(db, "grants", id),
      (snap) => setGrant(snap.exists() ? (snap.data() || {}) : null),
      () => setGrant(null)
    );
    return () => unsub();
  }, [mm?.ownerUid, mm?.managerUid]);

  useEffect(() => {
    let unsubDoc = null;
    const unsub = onAuthStateChanged(auth, (u) => {
      setIsAuthed(!!u);
      if (typeof unsubDoc === "function") {
        unsubDoc();
        unsubDoc = null;
      }
      if (!u) {
        setUserProfile(null);
        return;
      }
      unsubDoc = onSnapshot(doc(db, "users", u.uid), (snap) => {
        setUserProfile(snap.exists() ? (snap.data() || {}) : null);
      });
    });
    return () => {
      if (typeof unsubDoc === "function") unsubDoc();
      unsub();
    };
  }, []);

  const visible = isAuthed && !shouldHide(pathname);

  useEffect(() => {
    if (visible) document.body.classList.add("has-mobile-footer");
    else document.body.classList.remove("has-mobile-footer");
    return () => document.body.classList.remove("has-mobile-footer");
  }, [visible]);

  if (!visible) return null;

  function doLogout(e) {
    e.preventDefault();
    signOut(auth).finally(() => window.location.assign("/login"));
  }

  // helper to keep <a> + your styles, but navigate client-side
  const go = (e, path) => { e.preventDefault(); navigate(path); };

  const isManagerView = !!mm?.ownerUid && !!mm?.managerUid;
  const showHome = isManagerView && canManagerSeePage(grant, "home");
  const showContacts = !isManagerView || canManagerSeePage(grant, "contacts");
  const showDocs = isManagerView && canManagerSeePage(grant, "important_documents");
  const showSupport = !isManagerView || canManagerSeePage(grant, "support_ticket");

  const hasPersonal =
    userProfile?.personalAccount?.active === true ||
    userProfile?.subscriptionStatus === "active" ||
    userProfile?.subscription?.status === "active" ||
    userProfile?.subscription?.active === true;

  const tenantId =
    userProfile?.businessAccount?.tenantId ||
    userProfile?.primaryTenantId ||
    userProfile?.tenantId ||
    "";

  const tenantRole = String(
    userProfile?.businessAccount?.role ||
    userProfile?.tenantRole ||
    userProfile?.role ||
    ""
  ).toLowerCase();

  const hasBusiness =
    !!tenantId &&
    ["owner", "hr_admin", "employee"].includes(tenantRole);

  return (
    <nav className="footer-nav" aria-label="Primary navigation">
      {isManagerView ? (
        <>
          {(showHome || showContacts) ? (
            <a
              className="f-link"
              href={showHome ? "/" : "/contacts"}
              onClick={(e) => go(e, showHome ? "/" : "/contacts")}
            >
              {showHome ? <IconHome /> : <IconBook />}
              <span>{showHome ? "Home" : "Contacts"}</span>
            </a>
          ) : null}

          {showDocs ? (
            <a className="f-link" href="/legal/important-documents" onClick={(e) => go(e, "/legal/important-documents")}>
              <IconDoc />
              <span>Docs</span>
            </a>
          ) : null}

          <a className="f-link" href="/manager-select" onClick={(e) => go(e, "/manager-select")}>
            <IconUsers />
            <span>Accounts</span>
          </a>

          {showSupport ? (
            <a className="f-link" href="/support" onClick={(e) => go(e, "/support")}
            >
              <IconHelp />
              <span>Support</span>
            </a>
          ) : null}
        </>
      ) : (
        <>
          <a className="f-link" href="/" onClick={(e) => go(e, "/")}>
            <IconHome />
            <span>Home</span>
          </a>

          {hasBusiness ? (
            <a className="f-link" href="/account-managers/managed" onClick={(e) => go(e, "/account-managers/managed")}>
              <IconUsers />
              <span>Accounts</span>
            </a>
          ) : (
            <a className="f-link" href="/account-managers" onClick={(e) => go(e, "/account-managers")}>
              <IconUsers />
              <span>Managers</span>
            </a>
          )}

          {hasPersonal ? (
            <a className="fab-ewill" href="/legal/secure-e-will" onClick={(e) => go(e, "/legal/secure-e-will")} aria-label="Create E-Will">
              + E-Will
            </a>
          ) : (
            <a className="fab-ewill" href="/contacts" onClick={(e) => go(e, "/contacts")} aria-label="Open Contacts">
              + Contacts
            </a>
          )}

          <a className="f-link" href="/support" onClick={(e) => go(e, "/support")}>
            <IconHelp />
            <span>Support</span>
          </a>
        </>
      )}

      <button className="f-link" onClick={doLogout} type="button">
        <IconLogout />
        <span>Logout</span>
      </button>
    </nav>
  );

}
